import React, { useState } from 'react';
import { useLang } from '../../contexts/LangContext';
import { apartmentsApi, type CreateApartmentMemberData } from '../../api/apartmentsApi';

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  apartmentId: string;
  existingMembers: Array<{ role?: string; fullName?: string }>;
}

const isOwnerRole = (role?: string) =>
  role === 'CHU_HO' || role === 'OWNER' || role === 'CORPORATE_REPRESENTATIVE';

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  apartmentId,
  existingMembers,
}) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateApartmentMemberData>({
    apartmentId,
    fullName: '',
    dateOfBirth: '',
    relationshipCode: '',
    role: 'NHAN_KHAU',
    isPrimary: false,
    email: '',
    phone: '',
    citizenId: '',
  });

  const existingOwner = existingMembers?.find((member) => isOwnerRole(member.role));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.fullName?.trim()) {
      setError(t('pleaseEnterFullName'));
      return;
    }

    if (isOwnerRole(formData.role) && existingOwner) {
      setError('Căn hộ này đã có chủ hộ. Vui lòng chọn vai trò khác hoặc chỉnh sửa chủ hộ hiện tại.');
      return;
    }

    try {
      setLoading(true);
      await apartmentsApi.createApartmentMember({
        ...formData,
        fullName: formData.fullName.trim(),
        dateOfBirth: formData.dateOfBirth || undefined,
        relationshipCode: formData.relationshipCode || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        citizenId: formData.citizenId || undefined,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError('Có lỗi xảy ra khi thêm thành viên');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{t('addMemberTitle')}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('fullNameMember')} *</label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('dateOfBirth')}</label>
            <input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('residentRole')}</label>
            <select name="role" value={formData.role} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="CHU_HO">{t('residentRoleOwner')}</option>
              <option value="NHAN_KHAU">{t('residentRoleMember')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('relationshipRole')}</label>
            <input
              type="text"
              name="relationshipCode"
              value={formData.relationshipCode || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('email')}</label>
            <input type="text" name="email" value={formData.email || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('phone')}</label>
            <input type="tel" name="phone" value={formData.phone || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('citizenId')}</label>
            <input type="text" name="citizenId" value={formData.citizenId || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-md">{t('close')}</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-md disabled:opacity-50">
              {loading ? t('saving') : t('save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMemberModal;
